import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CaqRequestService } from '../../services/caq-request/caq-request.service';

export type DownloadRota =
  | { tipo: 'rota'; texto: string; rota: string }
  | { tipo: 'acao'; texto: string; acao: () => void };

export type FormatoResultado = 'PDF' | 'CSV';

@Injectable({
  providedIn: 'root',
})
export class LayoutResultadoService {
  public ano = new BehaviorSubject<string>('');
  public pais = new BehaviorSubject<string>('');
  public estado = new BehaviorSubject<string>('');
  public cidade = new BehaviorSubject<string>('');
  public editavel = new BehaviorSubject<boolean>(false);
  public rotaResultado = new BehaviorSubject<string>('');
  public corTextoTitulo = new BehaviorSubject<string>('');
  public corBordaTitulo = new BehaviorSubject<string>('');
  public tituloResultado = new BehaviorSubject<string>('');
  public redeSelecionada = new BehaviorSubject<string>('');
  public rotaApresentacao = new BehaviorSubject<string>('');
  public rotaEdicaoSimulacao = new BehaviorSubject<string>('');
  public rotaFiltroLocalidade = new BehaviorSubject<string>('');
  public rotaIndicadoresLocais = new BehaviorSubject<string>('');
  public rotaDownloadResultado = new BehaviorSubject<string>('');
  public downloads = new BehaviorSubject<DownloadRota[]>([]);
  public textoComoCitar = new BehaviorSubject<string>('');
  public imagemFundo = new BehaviorSubject<string>(
    'assets/ilustrations/bridge-mountain.svg',
  );
  public corTextoRodape = new BehaviorSubject<string>('');
  public corBotaoRodape = new BehaviorSubject<string>('');
  public corBordaBotaoRodape = new BehaviorSubject<string>('');

  constructor(private caqRequestService: CaqRequestService) {}

  setAno(ano: string) {
    this.ano.next(ano);
  }

  getAno(): Observable<string> {
    return this.ano.asObservable();
  }

  setPais(pais: string) {
    this.pais.next(pais);
  }

  getPais(): Observable<string> {
    return this.pais.asObservable();
  }

  setEstado(estado: string) {
    this.estado.next(estado);
  }

  getEstado(): Observable<string> {
    return this.estado.asObservable();
  }

  setCidade(cidade: string) {
    this.cidade.next(cidade);
  }

  getCidade(): Observable<string> {
    return this.cidade.asObservable();
  }

  setEditavel(editavel: boolean) {
    this.editavel.next(editavel);
  }

  getEditavel(): Observable<boolean> {
    return this.editavel.asObservable();
  }

  setRotaResultado(rota: string) {
    this.rotaResultado.next(rota);
  }

  getRotaResultado(): Observable<string> {
    return this.rotaResultado.asObservable();
  }

  setTitulo(tituloResultado: string) {
    this.tituloResultado.next(tituloResultado);
  }

  getTitulo(): Observable<string> {
    return this.tituloResultado.asObservable();
  }

  setCorBordaTitulo(tituloResultado: string) {
    this.corBordaTitulo.next(tituloResultado);
  }

  getCorBordaTitulo(): Observable<string> {
    return this.corBordaTitulo.asObservable();
  }

  setCorTexto(tituloResultado: string) {
    this.corTextoTitulo.next(tituloResultado);
  }

  getCorTexto(): Observable<string> {
    return this.corTextoTitulo.asObservable();
  }

  setRotaIndicadoresLocais(indicadores: string) {
    this.rotaIndicadoresLocais.next(indicadores);
  }

  getRotaIndicadoresLocais(): Observable<string> {
    return this.rotaIndicadoresLocais.asObservable();
  }

  setRedeSelecionada(redeSelecionada: string) {
    this.redeSelecionada.next(redeSelecionada);
  }

  getRedeSelecionada(): Observable<string> {
    return this.redeSelecionada.asObservable();
  }

  setRotaFiltroLocalidade(rota: string) {
    this.rotaFiltroLocalidade.next(rota);
  }

  getRotaFiltroLocalidade(): Observable<string> {
    return this.rotaFiltroLocalidade.asObservable();
  }

  setRotaDownloadResultado(rota: string) {
    this.rotaDownloadResultado.next(rota);
  }

  getRotaDownloadResultado(): Observable<string> {
    return this.rotaDownloadResultado.asObservable();
  }

  setRotaEdicaoSimulacao(rota: string) {
    this.rotaEdicaoSimulacao.next(rota);
  }

  getRotaEdicaoSimulacao(): Observable<string> {
    return this.rotaEdicaoSimulacao.asObservable();
  }

  setRotaApresentacao(rota: string) {
    this.rotaApresentacao.next(rota);
  }

  getRotaApresentacao(): Observable<string> {
    return this.rotaApresentacao.asObservable();
  }

  setDownloads(downloads: DownloadRota[]) {
    this.downloads.next(downloads);
  }

  getDownloads(): Observable<DownloadRota[]> {
    return this.downloads.asObservable();
  }

  setTextoComoCitar(textoComoCitar: string) {
    this.textoComoCitar.next(textoComoCitar);
  }

  getTextoComoCitar(): Observable<string> {
    return this.textoComoCitar.asObservable();
  }

  setImagemFundo(imagemFundo: string) {
    this.imagemFundo.next(imagemFundo);
  }

  getImagemFundo(): Observable<string> {
    return this.imagemFundo.asObservable();
  }

  getImagemFundoAtual(): string {
    return this.imagemFundo.value;
  }

  setCorTextoRodape(corTextoRodape: string) {
    this.corTextoRodape.next(corTextoRodape);
  }

  getCorTextoRodape(): Observable<string> {
    return this.corTextoRodape.asObservable();
  }

  setCorBotaoRodape(corBotaoRodape: string) {
    this.corBotaoRodape.next(corBotaoRodape);
  }

  getCorBotaoRodape(): Observable<string> {
    return this.corBotaoRodape.asObservable();
  }

  setCorBordaBotaoRodape(corBordaBotaoRodape: string) {
    this.corBordaBotaoRodape.next(corBordaBotaoRodape);
  }

  getCorBordaBotaoRodape(): Observable<string> {
    return this.corBordaBotaoRodape.asObservable();
  }

  public baixarResultado(
    rota: string,
    dadosRequisicao: any,
    formato: FormatoResultado = 'CSV',
  ): Observable<Blob> {
    const url = `${environment.apiUrl}/${rota}?formato=${formato}`;
    return this.caqRequestService.postBlob(url, dadosRequisicao);
  }
}
